import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { tutorAPI, subjectsAPI, studentAPI } from '../lib/api';
import { formatNextAvailable } from '../lib/availabilityUtils';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve tutor profile photo URLs (handles relative /api/... paths)
const getTutorPhotoUrl = (photoUrl) => {
  if (!photoUrl) return null;
  if (photoUrl.startsWith('http')) return photoUrl;
  if (photoUrl.startsWith('/')) return `${API_URL}${photoUrl}`;
  return photoUrl;
};

import { 
  GraduationCap, 
  Search, 
  Star, 
  Clock, 
  Filter,
  Loader2,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  User,
  LogOut,
  MessageCircle,
  CreditCard,
  Calendar,
  AlertTriangle,
  Mail,
  FileText,
  ShieldCheck
} from 'lucide-react';

const TutorsPage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Selected tutor for dialog
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [showTutorDialog, setShowTutorDialog] = useState(false);

  // Booking eligibility state
  const [bookingEligibility, setBookingEligibility] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(false);

  // Filters
  const [selectedSubject, setSelectedSubject] = useState(searchParams.get('subject') || '');
  const [selectedLevel, setSelectedLevel] = useState(searchParams.get('level') || '');
  const [minRating, setMinRating] = useState(searchParams.get('rating') || '');

  useEffect(() => {
    loadSubjects();
  }, []);

  useEffect(() => {
    loadTutors();
  }, [page, selectedSubject, selectedLevel, minRating]);

  // Load booking eligibility when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadBookingEligibility();
    }
  }, [isAuthenticated]);

  const loadSubjects = async () => {
    try {
      const response = await subjectsAPI.list();
      setSubjects(response.data.subjects);
      setGradeLevels(response.data.grade_levels);
    } catch (error) {
      console.error('Error loading subjects:', error);
    }
  };

  const loadBookingEligibility = async () => {
    setLoadingEligibility(true);
    try {
      const response = await studentAPI.getBookingEligibility();
      setBookingEligibility(response.data);
    } catch (error) {
      console.error('Error loading booking eligibility:', error);
      // Default to eligible if API fails
      setBookingEligibility({ eligible: true, issues: [] });
    } finally {
      setLoadingEligibility(false);
    }
  };

  const loadTutors = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12 };
      if (selectedSubject) params.subject = selectedSubject;
      if (selectedLevel) params.grade_level = selectedLevel;
      if (minRating) params.min_rating = parseFloat(minRating);

      const response = await tutorAPI.list(params);
      setTutors(response.data.tutors);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error loading tutors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (type, value) => {
    setPage(1);
    if (type === 'subject') {
      setSelectedSubject(value === 'all' ? '' : value);
    } else if (type === 'level') {
      setSelectedLevel(value === 'all' ? '' : value);
    } else if (type === 'rating') {
      setMinRating(value === 'all' ? '' : value);
    }
  };

  const clearFilters = () => {
    setSelectedSubject('');
    setSelectedLevel('');
    setMinRating('');
    setPage(1);
  };

  // Handle back navigation - go to dashboard if authenticated, otherwise homepage
  const handleBack = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="tutors-page">
      <SEO 
        title="Find Expert Tutors for Math, Physics & Chemistry"
        description="Browse qualified tutors specializing in Mathematics, Physics, and Chemistry. Find expert help for Calculus, Advanced Functions, Linear Algebra, Physics, and Chemistry at high school and university levels."
        keywords="find tutors, math tutor, physics tutor, chemistry tutor, calculus help, linear algebra tutor, advanced functions tutor, university tutoring"
      />
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={handleBack} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl font-semibold text-slate-900">
                  Find Tutors
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard">
                    <Button variant="ghost" className="rounded-full gap-2" data-testid="dashboard-link">
                      <User className="h-4 w-4" />
                      <span className="hidden sm:inline">{user?.name?.split(' ')[0]}</span>
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    className="rounded-full gap-2" 
                    onClick={logout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden sm:inline">Logout</span>
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="rounded-full">Sign In</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="rounded-full bg-slate-900 hover:bg-slate-800">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Page Title - H1 for SEO */}
        <h1 className="font-heading text-3xl md:text-4xl font-medium text-slate-900 mb-6">
          Find Expert Tutors
        </h1>
        
        {/* Filters */}
        <div className="bg-white rounded-xl p-6 mb-8 border border-stone-100 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-slate-600" />
            <h2 className="font-semibold text-slate-900">Filter Tutors</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            <Select value={selectedSubject || 'all'} onValueChange={(v) => handleFilterChange('subject', v)}>
              <SelectTrigger data-testid="subject-filter">
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {subjects.map((subject) => (
                  <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedLevel || 'all'} onValueChange={(v) => handleFilterChange('level', v)}>
              <SelectTrigger data-testid="level-filter">
                <SelectValue placeholder="All Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                {gradeLevels.map((level) => (
                  <SelectItem key={level.id} value={level.id}>{level.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={minRating || 'all'} onValueChange={(v) => handleFilterChange('rating', v)}>
              <SelectTrigger data-testid="rating-filter">
                <SelectValue placeholder="Any Rating" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Rating</SelectItem>
                <SelectItem value="4.5">4.5+ Stars</SelectItem>
                <SelectItem value="4">4+ Stars</SelectItem>
                <SelectItem value="3.5">3.5+ Stars</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant="outline" 
              onClick={clearFilters}
              className="rounded-lg"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-indigo-700" />
          </div>
        ) : tutors.length === 0 ? (
          <div className="text-center py-20">
            <GraduationCap className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-slate-900 mb-2">No tutors found</h3>
            <p className="text-slate-600 mb-4">Try adjusting your filters or check back later.</p>
            <Button onClick={clearFilters} variant="outline" className="rounded-full">
              Clear Filters
            </Button>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {tutors.map((tutor, index) => (
                <motion.div
                  key={tutor.user_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className="overflow-hidden hover:shadow-float transition-all cursor-pointer border-stone-100"
                    onClick={() => {
                      setSelectedTutor(tutor);
                      setShowTutorDialog(true);
                    }}
                    data-testid={`tutor-card-${tutor.profile_id || tutor.user_id}`}
                  >
                    <CardContent className="p-6">
                      {/* Header with photo and name */}
                      <div className="flex items-start gap-4">
                        <Avatar className="h-16 w-16 border-2 border-stone-100">
                          <AvatarImage src={getTutorPhotoUrl(tutor.profile_photo_url) || tutor.picture} alt={tutor.name} />
                          <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xl">
                            {tutor.name?.charAt(0) || 'T'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-slate-900 truncate">
                            {tutor.name}
                          </h3>
                          {tutor.headline && (
                            <p className="text-sm text-slate-600 truncate mt-0.5">
                              {tutor.headline}
                            </p>
                          )}
                          {tutor.rating_average > 0 && (
                            <div className="flex items-center gap-1 text-sm text-amber-500 mt-1">
                              <Star className="h-4 w-4 fill-current" />
                              <span>{tutor.rating_average?.toFixed(1)}</span>
                              <span className="text-slate-400">
                                ({tutor.total_reviews || 0} reviews)
                              </span>
                            </div>
                          )}
                          {tutor.experience_years > 0 && (
                            <p className="text-xs text-slate-500 mt-1">
                              {tutor.experience_years} years experience
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Subjects taught */}
                      <div className="mt-4">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                          Subjects
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {tutor.subjects?.slice(0, 3).map((subject) => (
                            <Badge key={subject} variant="secondary" className="bg-indigo-50 text-indigo-700">
                              {subject}
                            </Badge>
                          ))}
                          {tutor.subjects?.length > 3 && (
                            <Badge variant="secondary" className="bg-stone-100 text-slate-600">
                              +{tutor.subjects.length - 3}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Educational Background */}
                      {tutor.educational_background && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                            Education
                          </p>
                          <p className="text-sm text-slate-700 line-clamp-1">
                            {tutor.educational_background}
                          </p>
                        </div>
                      )}

                      {/* Bio / Professional introduction */}
                      <p className="mt-3 text-sm text-slate-600 line-clamp-2">
                        {tutor.bio || 'Experienced tutor ready to help you succeed.'}
                      </p>

                      {/* Availability Preview */}
                      <div className="mt-4 pt-3 border-t border-stone-100">
                        {(() => {
                          const nextAvailable = formatNextAvailable(tutor.availability);
                          return nextAvailable ? (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100">
                                <Calendar className="h-4 w-4 text-green-600" />
                              </div>
                              <div>
                                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                                  Next Available
                                </p>
                                <p className="text-sm font-semibold text-green-700">
                                  {nextAvailable}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100">
                                <Clock className="h-4 w-4 text-slate-400" />
                              </div>
                              <div>
                                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                                  Availability
                                </p>
                                <p className="text-sm text-slate-600">
                                  Contact admin for scheduling
                                </p>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-4 text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Tutor Selection Dialog */}
      <Dialog open={showTutorDialog} onOpenChange={setShowTutorDialog}>
        <DialogContent className="sm:max-w-md" data-testid="tutor-selection-dialog">
          <DialogHeader>
            <DialogTitle className="text-center">Book a Session</DialogTitle>
          </DialogHeader>
          
          {selectedTutor && (
            <div className="space-y-6 py-4">
              {/* Tutor Info */}
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 border-2 border-indigo-100">
                  <AvatarImage src={getTutorPhotoUrl(selectedTutor.profile_photo_url)} alt={selectedTutor.name} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xl">
                    {selectedTutor.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">
                    {selectedTutor.name}
                  </h3>
                  {selectedTutor.headline && (
                    <p className="text-sm text-slate-600">{selectedTutor.headline}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedTutor.subjects?.slice(0, 3).map(s => (
                      <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Eligibility Warning - Show if not eligible */}
              {isAuthenticated && bookingEligibility && !bookingEligibility.eligible && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="dialog-eligibility-warning">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-900 text-sm mb-2">
                        Complete these steps first
                      </h4>
                      <div className="space-y-2">
                        {bookingEligibility.issues.map((issue, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            {issue.type === 'email_not_verified' && (
                              <>
                                <Mail className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                <span className="text-amber-800">{issue.message}</span>
                              </>
                            )}
                            {(issue.type === 'agreement_required' || issue.type === 'consent_required') && (
                              <>
                                <FileText className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                <span className="text-amber-800">{issue.message}</span>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Eligibility Success - Show if eligible */}
              {isAuthenticated && bookingEligibility?.eligible && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2" data-testid="dialog-eligibility-success">
                  <ShieldCheck className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700">You're all set to book</span>
                </div>
              )}

              <p className="text-center text-slate-600">
                How would you like to proceed with <strong>{selectedTutor.name}</strong>?
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                {/* Pay Now Button - Disabled if not eligible */}
                <Button
                  onClick={() => {
                    if (!isAuthenticated) {
                      navigate('/login');
                    } else if (bookingEligibility?.eligible) {
                      navigate('/pricing');
                    }
                  }}
                  className={`w-full rounded-full py-6 ${
                    isAuthenticated && !bookingEligibility?.eligible
                      ? 'bg-slate-300 hover:bg-slate-300 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                  disabled={isAuthenticated && !bookingEligibility?.eligible}
                  data-testid="pay-now-btn"
                >
                  {loadingEligibility ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="h-5 w-5 mr-2" />
                  )}
                  {!isAuthenticated ? 'Sign In to Book' : 'Pay Now'}
                </Button>

                {/* Action buttons for ineligible users */}
                {isAuthenticated && bookingEligibility && !bookingEligibility.eligible && (
                  <div className="flex flex-col gap-2">
                    {bookingEligibility.issues.some(i => i.type === 'email_not_verified') && (
                      <Button
                        onClick={() => navigate('/verify-email')}
                        variant="outline"
                        className="w-full rounded-full py-5 border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="dialog-verify-email-btn"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Verify Your Email
                      </Button>
                    )}
                    {bookingEligibility.issues.some(i => i.type === 'agreement_required' || i.type === 'consent_required') && (
                      <Button
                        onClick={() => navigate('/student/agreement')}
                        variant="outline"
                        className="w-full rounded-full py-5 border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="dialog-complete-agreement-btn"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        {bookingEligibility.issues.some(i => i.type === 'consent_required') 
                          ? 'Complete Parent Consent' 
                          : 'Sign Student Agreement'}
                      </Button>
                    )}
                  </div>
                )}

                <Button
                  onClick={() => navigate('/contact')}
                  variant="outline"
                  className="w-full rounded-full py-6"
                  data-testid="contact-admin-btn"
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Contact Admin
                </Button>
              </div>

              <p className="text-xs text-center text-slate-500">
                {isAuthenticated && !bookingEligibility?.eligible 
                  ? 'Complete the required steps above to unlock booking.'
                  : 'Contact us if you have questions before booking, or proceed to payment to schedule your session.'}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TutorsPage;
